import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Button, Select, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRole } from '../../store/roleContext';
import { Role, Permission } from '../../types/roles';
import { fetchPermissions } from '../../api/permissionApi';
import { translatePermission } from '../../utils/permissionTranslator';
import { splitPermission } from '../../constants/permissions';
import { useAuth } from '../../store/AuthContext';

const { Text } = Typography;

interface RoleFormProps {
    editRole?: Role | null;
    onFinishEdit?: () => void;
}

const RoleForm: React.FC<RoleFormProps> = ({ editRole, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addRole, updateRole } = useRole();
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const { t } = useTranslation();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchPermissions().then((data) => setPermissions(data));
    }, []);

    useEffect(() => {
        if (editRole) {
            form.setFieldsValue(editRole);
        } else {
            form.resetFields();
        }
    }, [editRole, form]);

    /**
     * The system ships well over two hundred permissions, so a flat list is
     * unusable. Group them by feature and let the whole group be picked at once.
     */
    const groups = useMemo(() => {
        const byGroup = new Map<string, Permission[]>();
        for (const permission of permissions) {
            const { group } = splitPermission(permission.name);
            const list = byGroup.get(group);
            if (list) list.push(permission);
            else byGroup.set(group, [permission]);
        }
        return Array.from(byGroup.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([group, items]) => ({
                group,
                items: items.sort((a, b) => a.name.localeCompare(b.name)),
            }));
    }, [permissions]);

    const options = useMemo(
        () =>
            groups.map(({ group, items }) => ({
                label: t(`permission_group.${group}`, { defaultValue: group }),
                options: items.map(permission => ({
                    value: permission.id,
                    label: translatePermission(permission.name, t),
                    // Searching by the raw name works even when a translation exists.
                    title: permission.name,
                })),
            })),
        [groups, t]
    );

    const selectGroup = (group: string) => {
        const ids = groups.find(g => g.group === group)?.items.map(p => p.id) ?? [];
        const current: string[] = form.getFieldValue('permissions') || [];
        form.setFieldsValue({ permissions: Array.from(new Set([...current, ...ids])) });
    };

    const selectAll = () => {
        form.setFieldsValue({ permissions: permissions.map(p => p.id) });
    };

    const clearAll = () => form.setFieldsValue({ permissions: [] });

    const onFinish = (values: Role) => {
        if (editRole) {
            updateRole({ ...editRole, ...values });
            onFinishEdit?.();
        } else {
            addRole(values);
            form.resetFields();
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
                name="name"
                label={t('role_name')}
                rules={[{ required: true, message: t('please_enter_role_name') || '' }]}
            >
                <Input placeholder={t('role_name') || ''} />
            </Form.Item>

            <Form.Item name="permissions" label={t('select_permissions')}>
                <Select
                    mode="multiple"
                    placeholder={t('select_permissions') || ''}
                    options={options}
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                        `${option?.label ?? ''} ${(option as any)?.title ?? ''}`
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    maxTagCount="responsive"
                    allowClear
                />
            </Form.Item>

            <Form.Item label={t('quick_select') || 'Quick select'}>
                <Space size={[4, 8]} wrap>
                    <Tag.CheckableTag checked={false} onChange={selectAll}>
                        {t('select_all') || 'Select all'}
                    </Tag.CheckableTag>
                    <Tag.CheckableTag checked={false} onChange={clearAll}>
                        {t('clear_all') || 'Clear all'}
                    </Tag.CheckableTag>
                    {groups.map(({ group, items }) => (
                        <Tag.CheckableTag
                            key={group}
                            checked={false}
                            onChange={() => selectGroup(group)}
                        >
                            {t(`permission_group.${group}`, { defaultValue: group })} ({items.length})
                        </Tag.CheckableTag>
                    ))}
                </Space>
            </Form.Item>

            <Form.Item shouldUpdate noStyle>
                {() => (
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                        {(form.getFieldValue('permissions') || []).length} / {permissions.length}{' '}
                        {t('permissions_selected') || 'permissions selected'}
                    </Text>
                )}
            </Form.Item>

            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    block
                    disabled={editRole ? !hasPermission('role.update') : !hasPermission('role.create')}
                >
                    {editRole ? t('update_role') : t('create_role')}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default RoleForm;
